import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { AgentInterrupt } from "../types/ai_agent";
import type { AgentInterruptActionRequest } from "../types/ai_agent";
import type { ChatInterruptMetadata } from "../types/chat";

import "./hitl_ui.css";

type OnRampPixTransaction = {
  transactionId: string;
  status: string;
  codePix: string;
  inputAmount: string;
  inputCurrency: string;
  outputAmount: string;
  outputCurrency: string;
  expiresAt: string;
};

function getOnRampPixTransaction(
  metadata?: ChatInterruptMetadata,
): OnRampPixTransaction | null {
  const output = metadata?.toolOutput;

  if (!output || typeof output !== "object") return null;

  const record = output as Record<string, unknown>;
  const transaction = record.transaction;

  if (!transaction || typeof transaction !== "object") return null;

  const tx = transaction as Record<string, unknown>;

  if (typeof tx.codePix !== "string" || tx.codePix.length === 0) return null;

  return tx as unknown as OnRampPixTransaction;
}

function getOffRampPixTransactionId(
  metadata?: ChatInterruptMetadata,
): string | null {
  const output = metadata?.toolOutput;

  if (!output || typeof output !== "object") return null;

  const record = output as Record<string, unknown>;
  const transaction = record.transaction;

  if (!transaction || typeof transaction !== "object") return null;

  const tx = transaction as Record<string, unknown>;

  return typeof tx.id === "string" && tx.id.length > 0 ? tx.id : null;
}

type HitlStatus =
  | "pending"
  | "submitting"
  | "rejected"
  | "expired"
  | "approved";

type HitlDecision = {
  name: string;
  decision: "approve" | "reject" | "edit";
};

type HitlRendererCtx = {
  interrupt: AgentInterrupt;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
};

type HitlRenderer = (
  ctx: HitlRendererCtx & { request: AgentInterruptActionRequest },
) => ReactNode;

function formatDecimal(v: unknown) {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));

  if (!Number.isFinite(n)) {
    return String(v ?? "—");
  }

  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
}

/**
 * Normaliza uma string decimal removendo sinal redundante, zeros à esquerda e
 * zeros à direita da parte fracionária. Retorna `null` se não for um decimal.
 * Trabalha sobre a string (e não sobre `parseFloat`) para não perder precisão
 * em casas decimais que o float64 não representa exatamente.
 */
function normalizeDecimal(value: string): string | null {
  const trimmed = value.trim();

  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(trimmed)) {
    return null;
  }

  const negative = trimmed.startsWith("-");
  const [intRaw = "", fracRaw = ""] = trimmed.replace(/^[+-]/, "").split(".");
  const int = intRaw.replace(/^0+(?=\d)/, "") || "0";
  const frac = fracRaw.replace(/0+$/, "");
  const isZero = int === "0" && frac === "";

  return `${negative && !isZero ? "-" : ""}${int}${frac ? `.${frac}` : ""}`;
}

/** Compara duas quantidades por VALOR: "1" e "1.0" são a mesma quantidade. */
function sameQuantity(a: string, b: string): boolean {
  const na = normalizeDecimal(a);
  const nb = normalizeDecimal(b);

  // Valor não-numérico (ex.: rótulo inesperado do backend): cai no literal.
  if (na === null || nb === null) {
    return a.trim() === b.trim();
  }

  return na === nb;
}

/**
 * Resolve um valor de expiração para timestamp em milissegundos.
 *
 * Formatos aceitos:
 *  - ISO 8601 com TZ explícita  → usa diretamente
 *  - ISO 8601 sem TZ (naive)    → assume UTC (evita offset de fuso-horário)
 *  - Número > 1e12              → epoch em ms
 *  - Número > 1e9               → epoch em segundos
 *  - Número pequeno             → delta relativo em segundos a partir de agora
 */
function parseExpiresAtMs(value: unknown): number {
  if (value == null || value === "") {
    return NaN;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    if (value > 1e12) return value;
    if (value > 1e9) return value * 1000;
    return Date.now() + value * 1000;
  }

  if (typeof value !== "string") {
    return NaN;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return NaN;
  }

  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
  const iso = hasTz ? trimmed : `${trimmed}Z`;
  const ms = Date.parse(iso);

  if (!Number.isNaN(ms)) {
    return ms;
  }

  const asNumber = Number(trimmed);

  if (Number.isFinite(asNumber)) {
    return parseExpiresAtMs(asNumber);
  }

  return NaN;
}

function TradeApprovalHitlCard({
  request,
  status,
  metadata,
  onDecision,
  onExpired,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  const pairName = String(args.pairName ?? "");
  const inputCurrency = String(args.inputCurrency ?? "");
  const inputAmount = String(args.inputAmount ?? "");
  const outputCurrency = String(args.outputCurrency ?? "");
  const outputAmount = String(args.outputAmount ?? "");
  const basePrice = String(args.basePrice ?? "");
  const fee = String(args.fee ?? "");
  const feePercent = String(args.feePercent ?? "");
  const quoteId = String(args.quoteId ?? "");

  // `side` só existe em ordens com direção (market/limit). O swap usa este
  // mesmo card e NÃO tem direção — nesse caso os rótulos ficam neutros
  // ("ordem"), nunca "compra".
  const side = String(args.side ?? "").trim().toUpperCase();
  const isBuy = side === "BUY";
  const isSell = side === "SELL";
  const sideLabel = isBuy ? "Compra" : isSell ? "Venda" : side;
  const actionNoun = isBuy ? "compra" : isSell ? "venda" : "ordem";
  const executedBadge = isBuy ? "Comprado" : isSell ? "Vendido" : "Executada";
  const actionNounCapitalized = actionNoun.charAt(0).toUpperCase() +
    actionNoun.slice(1);

  const expiresAtSource = metadata?.expiresIn ?? args.expiresIn;
  const expiresAt = parseExpiresAtMs(expiresAtSource);
  const hasExpiry = Number.isFinite(expiresAt) && expiresAt > 0;

  const [now, setNow] = useState(() => Date.now());

  // Evita disparar onExpired mais de uma vez
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!hasExpiry) {
      return;
    }

    const handle = window.setInterval(() => setNow(Date.now()), 500);

    return () => window.clearInterval(handle);
  }, [hasExpiry]);

  const secondsLeft = hasExpiry
    ? Math.max(0, Math.floor((expiresAt - now) / 1000))
    : 0;

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  // Desfecho da tool registrado pelo backend após a decisão do usuário.
  // `approved + toolFailed` é um estado válido: usuário aprovou, mas a
  // execução posterior (ex.: ordem de mercado) falhou. A UI mostra os
  // dois fatos distintamente em vez de mentir "Comprado".
  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  // Status finais do backend têm precedência sobre o timer de expiração
  const hasFinalStatus = isRejected || isApproved;

  // O interrupt pode já ter vindo do backend como "expired"
  const isExpiredByStatus = status === "expired";
  const isExpiredByTimer = !hasFinalStatus && hasExpiry && secondsLeft <= 0;
  const expired = isExpiredByStatus || isExpiredByTimer;

  // Notifica o pai uma única vez quando o timer chega a zero
  useEffect(() => {
    if (isExpiredByTimer && !expiredFiredRef.current) {
      expiredFiredRef.current = true;
      onExpired?.();
    }
  }, [isExpiredByTimer, onExpired]);

  const disableActions = submitting || isRejected || expired || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? executedBadge
    : isRejected
    ? "Cancelado"
    : expired
    ? "Expirado"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Aprovado, mas a execução falhou: ${failureReason}`
      : "Aprovado, mas a execução falhou."
    : isApproved
    ? `${actionNounCapitalized} confirmada.`
    : isRejected
    ? `${actionNounCapitalized} cancelada.`
    : submitting
    ? "Enviando decisão ao agente…"
    : expired
    ? "Cotação expirada. Peça uma nova simulação."
    : null;

  const handleConfirm = () => {
    if (disableActions) {
      return;
    }

    void onDecision({ name: request.name, decision: "approve" });
  };

  const handleCancel = () => {
    if (submitting || isRejected || expired) {
      return;
    }

    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : expired
    ? " hitlSimCard--expired"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? `Spot ${actionNoun} aprovada mas falhou na execução (HITL)`
            : isApproved
            ? `Spot ${actionNoun} confirmada (HITL)`
            : isRejected
            ? `Spot ${actionNoun} cancelada (HITL)`
            : expired
            ? `Spot ${actionNoun} expirada (HITL)`
            : `Aprovação de ${actionNoun} (HITL)`
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : expired
                ? "expired"
                : isSell
                ? "sell"
                : isBuy
                ? "buy"
                : "neutral"
            }`}
          >
            {approvedButFailed
              ? "!"
              : isApproved
              ? "✓"
              : expired
              ? "✕"
              : isSell
              ? "↘"
              : isBuy
              ? "↗"
              : "⇄"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Confirmar {actionNoun}{pairName ? ` — ${pairName}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Revise a simulação antes de executar a ordem."}
            </div>
          </div>

          {/* Countdown — só mostra quando pendente e com expiração */}
          {!badge && hasExpiry && (
            <span className="hitlSimCard-countdown">
              {secondsLeft}s
            </span>
          )}

          {/* Badge de estado final */}
          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : isRejected
                  ? "hitlSimCard-countdown--rejected"
                  : "hitlSimCard-countdown--expired"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-amounts">
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você paga</div>
            <div className="hitlSimCard-amountValue">
              {formatDecimal(inputAmount)} {inputCurrency}
            </div>
          </div>
          <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você recebe</div>
            <div className="hitlSimCard-amountValue">
              ≈ {formatDecimal(outputAmount)} {outputCurrency}
            </div>
          </div>
        </div>

        <div className="hitlSimCard-details">
          {side && (
            <div className="hitlSimCard-detail">
              <span>Ordem</span>
              <span>{sideLabel}</span>
            </div>
          )}
          <div className="hitlSimCard-detail">
            <span>Preço base</span>
            <span>{formatDecimal(basePrice)}</span>
          </div>
          <div className="hitlSimCard-detail">
            <span>Taxa</span>
            <span>
              {formatDecimal(fee)} {inputCurrency}
              {feePercent ? ` (${feePercent}%)` : ""}
            </span>
          </div>
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Quote ID</span>
            <span className="hitlSimCard-mono">{quoteId || "—"}</span>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              expired || approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !expired && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`hitlSimCard-btn hitlSimCard-btn--${
                isSell ? "confirmSell" : isBuy ? "confirmBuy" : "confirmNeutral"
              }`}
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : `Confirmar ${actionNoun}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function OnRampPixApprovalCard({
  request,
  status,
  metadata,
  onDecision,
  onExpired,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  const pairName = String(args.pairName ?? "");
  const inputCurrency = String(args.inputCurrency ?? "BRL");
  const inputAmount = String(args.inputAmount ?? "");
  const outputCurrency = String(args.outputCurrency ?? "USDT");
  const outputAmount = String(args.outputAmount ?? "");
  const basePrice = String(args.basePrice ?? "");
  const vetPrice = String(args.vetPrice ?? "");
  const fee = String(args.fee ?? "");
  const inputPaymentMethod = String(args.inputPaymentMethod ?? "PIX");
  const provider = String(args.provider ?? "");
  const quoteId = String(args.quoteId ?? "");

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const transaction = getOnRampPixTransaction(metadata);
  const showQr = isApproved && !!transaction;

  const quoteExpiresAtSource = metadata?.expiresIn ?? args.expiresIn ??
    args.quoteExpiresIn;
  const quoteExpiresAt = parseExpiresAtMs(quoteExpiresAtSource);
  const hasQuoteExpiry = Number.isFinite(quoteExpiresAt) && quoteExpiresAt > 0;

  const txExpiresAt = transaction
    ? new Date(transaction.expiresAt).getTime()
    : Number.NaN;
  const hasTxExpiry = Number.isFinite(txExpiresAt) && txExpiresAt > 0;

  // O timer ativo depende do estado: enquanto a cotação está em revisão usamos
  // a expiração da quote; após aprovação, passamos a usar a expiração da
  // transação PIX (se houver). Apenas um timer fica vivo por vez.
  const activeExpiresAt = showQr ? txExpiresAt : quoteExpiresAt;
  const hasActiveExpiry = showQr ? hasTxExpiry : hasQuoteExpiry;

  const [now, setNow] = useState(() => Date.now());
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!hasActiveExpiry) return;
    const handle = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(handle);
  }, [hasActiveExpiry, activeExpiresAt]);

  const secondsLeft = hasActiveExpiry
    ? Math.max(0, Math.floor((activeExpiresAt - now) / 1000))
    : 0;

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  const hasFinalStatusForQuote = isRejected || isApproved;
  const isExpiredByStatus = status === "expired";
  // Antes de aprovar: timer da quote vale e dispara `onExpired`.
  // Depois de aprovar com QR: timer vira o da própria transação PIX.
  const isExpiredByQuoteTimer = !hasFinalStatusForQuote && hasActiveExpiry &&
    secondsLeft <= 0 && !showQr;
  const isExpiredByTxTimer = showQr && hasActiveExpiry && secondsLeft <= 0;
  const isExpiredByTimer = isExpiredByQuoteTimer || isExpiredByTxTimer;
  const expired = isExpiredByStatus || isExpiredByTimer;

  useEffect(() => {
    // Só notifica o parent quando a *quote* expira sem decisão. Expiração
    // pós-aprovação (do PIX gerado) é local — o interrupt continua "approved".
    if (isExpiredByQuoteTimer && !expiredFiredRef.current) {
      expiredFiredRef.current = true;
      onExpired?.();
    }
  }, [isExpiredByQuoteTimer, onExpired]);

  const disableActions = submitting || isRejected || expired || isApproved;

  // Importante: quando o PIX gerado expira pós-aprovação, queremos "Expirado"
  // — não "Confirmado" — para não conflitar com a mensagem de erro abaixo.
  const badge = approvedButFailed
    ? "Falhou"
    : isExpiredByTxTimer
    ? "Expirado"
    : isApproved
    ? "Confirmado"
    : isRejected
    ? "Cancelado"
    : expired
    ? "Expirada"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Aprovado, mas a criação do PIX falhou: ${failureReason}`
      : "Aprovado, mas a criação do PIX falhou."
    : showQr && isExpiredByTxTimer
    ? "Código PIX expirado. Peça uma nova simulação."
    : isApproved && !showQr
    ? "Depósito confirmado. Gerando código PIX…"
    : isRejected
    ? "Depósito cancelado."
    : submitting
    ? "Enviando decisão ao agente…"
    : expired
    ? "Cotação expirada. Peça uma nova simulação."
    : null;

  const [pixCopied, setPixCopied] = useState(false);

  const handleCopyPix = async () => {
    if (!transaction) return;
    try {
      await navigator.clipboard.writeText(transaction.codePix);
      setPixCopied(true);
      window.setTimeout(() => setPixCopied(false), 1800);
    } catch (err) {
      console.warn("Falha ao copiar PIX:", err);
    }
  };

  const handleConfirm = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  const handleCancel = () => {
    if (submitting || isRejected || expired) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isExpiredByTxTimer
    ? " hitlSimCard--expired"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : expired
    ? " hitlSimCard--expired"
    : "";

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Depósito PIX aprovado mas falhou na execução (HITL)"
            : isApproved
            ? "Depósito PIX confirmado (HITL)"
            : isRejected
            ? "Depósito PIX cancelado (HITL)"
            : expired
            ? "Cotação PIX expirada (HITL)"
            : "Aprovação de depósito PIX (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : expired
                ? "expired"
                : "buy"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : expired ? "✕" : "PIX"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Confirmar depósito PIX{pairName ? ` — ${pairName}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Revise a cotação antes de gerar o código PIX."}
            </div>
          </div>

          {!badge && hasActiveExpiry && (
            <span className="hitlSimCard-countdown">
              {formatCountdown(secondsLeft)}
            </span>
          )}

          {badge && showQr && hasActiveExpiry && !expired && (
            <span className="hitlSimCard-countdown">
              {formatCountdown(secondsLeft)}
            </span>
          )}

          {badge && !(showQr && hasActiveExpiry && !expired) && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isExpiredByTxTimer
                  ? "hitlSimCard-countdown--expired"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : isRejected
                  ? "hitlSimCard-countdown--rejected"
                  : "hitlSimCard-countdown--expired"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-amounts">
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você paga</div>
            <div className="hitlSimCard-amountValue">
              {formatDecimal(inputAmount)} {inputCurrency}
            </div>
          </div>
          <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você recebe</div>
            <div className="hitlSimCard-amountValue">
              ≈ {formatDecimal(outputAmount)} {outputCurrency}
            </div>
          </div>
        </div>

        <div className="hitlSimCard-details">
          <div className="hitlSimCard-detail">
            <span>Inclui tarifas</span>
            <span>
              {formatDecimal(fee)} {inputCurrency}
            </span>
          </div>
          {basePrice && (
            <div className="hitlSimCard-detail">
              <span>Taxa base</span>
              <span>
                1.00 {outputCurrency} = {formatDecimal(basePrice)}{" "}
                {inputCurrency}
              </span>
            </div>
          )}
          {vetPrice && (
            <div className="hitlSimCard-detail">
              <span>Taxa efetiva</span>
              <span>
                1.00 {outputCurrency} = {formatDecimal(vetPrice)}{" "}
                {inputCurrency}
              </span>
            </div>
          )}
          <div className="hitlSimCard-detail">
            <span>Método</span>
            <span>{inputPaymentMethod}</span>
          </div>
          {provider && (
            <div className="hitlSimCard-detail">
              <span>Provider</span>
              <span>{provider}</span>
            </div>
          )}
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Quote ID</span>
            <span className="hitlSimCard-mono">{quoteId || "—"}</span>
          </div>
        </div>

        {showQr && transaction && (
          <div className="hitlSimCard-pix">
            <div className="hitlSimCard-pixQr">
              <QRCodeSVG
                value={transaction.codePix}
                size={180}
                level="M"
                marginSize={2}
              />
            </div>
            <button
              type="button"
              className="hitlSimCard-pixCodeBox"
              onClick={handleCopyPix}
              title="Copiar código PIX"
              disabled={isExpiredByTxTimer}
            >
              <span className="hitlSimCard-pixCodeText">
                {transaction.codePix}
              </span>
              <span className="hitlSimCard-pixCopyIcon" aria-hidden="true">
                {pixCopied ? "✓" : "⧉"}
              </span>
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleCopyPix}
              disabled={isExpiredByTxTimer}
            >
              {pixCopied ? "Copiado!" : "Copiar PIX copia-e-cola"}
            </button>
            <div className="hitlSimCard-pixMeta">
              <span>Transação</span>
              <span className="hitlSimCard-mono">
                {transaction.transactionId}
              </span>
            </div>
          </div>
        )}

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              expired || approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !expired && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar depósito"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function OffRampPixApprovalCard({
  request,
  status,
  metadata,
  onDecision,
  onExpired,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Off-ramp: inputCurrency é a cripto debitada; outputCurrency é sempre BRL.
  const pairName = String(args.pairName ?? "");
  const inputCurrency = String(args.inputCurrency ?? "USDT");
  const inputAmount = String(args.inputAmount ?? "");
  const outputCurrency = String(args.outputCurrency ?? "BRL");
  const outputAmount = String(args.outputAmount ?? "");
  const basePrice = String(args.basePrice ?? "");
  const vetPrice = String(args.vetPrice ?? "");
  const fee = String(args.fee ?? "");
  const provider = String(args.provider ?? "");
  const quoteId = String(args.quoteId ?? "");

  // Beneficiário PIX de destino: resolvido pelo backend na simulação de
  // off-ramp (mesmos dados do status KYC — banco, chave, titular, CPF).
  const beneficiary =
    args.beneficiary && typeof args.beneficiary === "object"
      ? (args.beneficiary as Record<string, unknown>)
      : null;
  const beneficiaryHolderName = beneficiary
    ? String(beneficiary.holderName ?? "")
    : "";
  const beneficiaryBankName = beneficiary
    ? String(beneficiary.bankName ?? "")
    : "";
  const beneficiaryPixKey = beneficiary ? String(beneficiary.pixKey ?? "") : "";
  const beneficiaryTaxId = beneficiary ? String(beneficiary.taxId ?? "") : "";

  const expiresAtSource = metadata?.expiresIn ?? args.expiresIn ??
    args.quoteExpiresIn;
  const expiresAt = parseExpiresAtMs(expiresAtSource);
  const hasExpiry = Number.isFinite(expiresAt) && expiresAt > 0;

  const [now, setNow] = useState(() => Date.now());
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!hasExpiry) return;
    const handle = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(handle);
  }, [hasExpiry]);

  const secondsLeft = hasExpiry
    ? Math.max(0, Math.floor((expiresAt - now) / 1000))
    : 0;

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const transactionId = getOffRampPixTransactionId(metadata);

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  const hasFinalStatus = isRejected || isApproved;
  const isExpiredByStatus = status === "expired";
  const isExpiredByTimer = !hasFinalStatus && hasExpiry && secondsLeft <= 0;
  const expired = isExpiredByStatus || isExpiredByTimer;

  useEffect(() => {
    if (isExpiredByTimer && !expiredFiredRef.current) {
      expiredFiredRef.current = true;
      onExpired?.();
    }
  }, [isExpiredByTimer, onExpired]);

  const disableActions = submitting || isRejected || expired || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Confirmado"
    : isRejected
    ? "Cancelado"
    : expired
    ? "Expirada"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Aprovado, mas o saque falhou: ${failureReason}`
      : "Aprovado, mas o saque falhou."
    : isApproved
    ? "Saque confirmado. O valor será creditado via PIX em instantes."
    : isRejected
    ? "Saque cancelado."
    : submitting
    ? "Enviando decisão ao agente…"
    : expired
    ? "Cotação expirada. Peça uma nova simulação."
    : null;

  const handleConfirm = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  const handleCancel = () => {
    if (submitting || isRejected || expired) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : expired
    ? " hitlSimCard--expired"
    : "";

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Saque PIX aprovado mas falhou na execução (HITL)"
            : isApproved
            ? "Saque PIX confirmado (HITL)"
            : isRejected
            ? "Saque PIX cancelado (HITL)"
            : expired
            ? "Cotação de saque PIX expirada (HITL)"
            : "Aprovação de saque PIX (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : expired
                ? "expired"
                : "buy"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : expired ? "✕" : "PIX"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Confirmar saque PIX{pairName ? ` — ${pairName}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Revise a cotação antes de enviar o PIX."}
            </div>
          </div>

          {!badge && hasExpiry && (
            <span className="hitlSimCard-countdown">
              {formatCountdown(secondsLeft)}
            </span>
          )}

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : isRejected
                  ? "hitlSimCard-countdown--rejected"
                  : "hitlSimCard-countdown--expired"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-amounts">
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você envia</div>
            <div className="hitlSimCard-amountValue">
              {formatDecimal(inputAmount)} {inputCurrency}
            </div>
          </div>
          <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você recebe</div>
            <div className="hitlSimCard-amountValue">
              ≈ {formatDecimal(outputAmount)} {outputCurrency}
            </div>
          </div>
        </div>

        <div className="hitlSimCard-details">
          <div className="hitlSimCard-detail">
            <span>Inclui tarifas</span>
            <span>
              {formatDecimal(fee)} {inputCurrency}
            </span>
          </div>
          {basePrice && (
            <div className="hitlSimCard-detail">
              <span>Taxa base</span>
              <span>
                1.00 {inputCurrency} = {formatDecimal(basePrice)}{" "}
                {outputCurrency}
              </span>
            </div>
          )}
          {vetPrice && (
            <div className="hitlSimCard-detail">
              <span>Taxa efetiva</span>
              <span>
                1.00 {inputCurrency} = {formatDecimal(vetPrice)}{" "}
                {outputCurrency}
              </span>
            </div>
          )}
          {provider && (
            <div className="hitlSimCard-detail">
              <span>Provider</span>
              <span>{provider}</span>
            </div>
          )}
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Quote ID</span>
            <span className="hitlSimCard-mono">{quoteId || "—"}</span>
          </div>
          {transactionId && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Transação</span>
              <span className="hitlSimCard-mono">{transactionId}</span>
            </div>
          )}
        </div>

        {beneficiary && (
          <div className="hitlSimCard-details">
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Destino do PIX</span>
              <span />
            </div>
            {beneficiaryHolderName && (
              <div className="hitlSimCard-detail">
                <span>Titular</span>
                <span>{beneficiaryHolderName}</span>
              </div>
            )}
            {beneficiaryBankName && (
              <div className="hitlSimCard-detail">
                <span>Banco</span>
                <span>{beneficiaryBankName}</span>
              </div>
            )}
            {beneficiaryPixKey && (
              <div className="hitlSimCard-detail hitlSimCard-detail--muted">
                <span>Chave PIX</span>
                <span className="hitlSimCard-mono">{beneficiaryPixKey}</span>
              </div>
            )}
            {beneficiaryTaxId && (
              <div className="hitlSimCard-detail hitlSimCard-detail--muted">
                <span>CPF/CNPJ</span>
                <span className="hitlSimCard-mono">{beneficiaryTaxId}</span>
              </div>
            )}
          </div>
        )}

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              expired || approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !expired && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar saque"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function getWithdrawRequestId(
  metadata?: ChatInterruptMetadata,
): string | null {
  const output = metadata?.toolOutput;

  if (!output || typeof output !== "object") return null;

  const record = output as Record<string, unknown>;
  const withdrawRequest = record.withdrawRequest;

  if (!withdrawRequest || typeof withdrawRequest !== "object") return null;

  const wr = withdrawRequest as Record<string, unknown>;

  return typeof wr.id === "string" && wr.id.length > 0 ? wr.id : null;
}

function WithdrawOnchainApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  const currency = String(args.coin ?? args.currency ?? "");
  const amount = String(args.amount ?? "");
  const network = String(args.networkName ?? args.network ?? "");
  const address = String(args.address ?? "");
  const tag = String(args.tag ?? "");
  const withdrawFee = String(args.withdrawFee ?? "");
  const totalDebit = String(args.totalDebit ?? "");

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const withdrawRequestId = getWithdrawRequestId(metadata);

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  const expired = status === "expired";

  const disableActions = submitting || isRejected || expired || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Registrado"
    : isRejected
    ? "Cancelado"
    : expired
    ? "Expirada"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Aprovado, mas o saque falhou: ${failureReason}`
      : "Aprovado, mas o saque falhou."
    : isApproved
    ? "Saque registrado. O envio on-chain será processado em instantes."
    : isRejected
    ? "Saque cancelado."
    : submitting
    ? "Enviando decisão ao agente…"
    : expired
    ? "Simulação expirada. Peça uma nova simulação."
    : null;

  const handleConfirm = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  const handleCancel = () => {
    if (submitting || isRejected || expired) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : expired
    ? " hitlSimCard--expired"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Saque on-chain aprovado mas falhou na execução (HITL)"
            : isApproved
            ? "Saque on-chain registrado (HITL)"
            : isRejected
            ? "Saque on-chain cancelado (HITL)"
            : "Aprovação de saque on-chain (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : expired
                ? "expired"
                : "buy"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : expired ? "✕" : "↗"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Confirmar saque{network ? ` — ${network}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Revise a simulação antes de registrar o saque on-chain."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : isRejected
                  ? "hitlSimCard-countdown--rejected"
                  : "hitlSimCard-countdown--expired"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-amounts">
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Você saca</div>
            <div className="hitlSimCard-amountValue">
              {formatDecimal(amount)} {currency}
            </div>
          </div>
          <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Total debitado</div>
            <div className="hitlSimCard-amountValue">
              {formatDecimal(totalDebit)} {currency}
            </div>
          </div>
        </div>

        <div className="hitlSimCard-details">
          <div className="hitlSimCard-detail">
            <span>Quantidade</span>
            <span>
              {formatDecimal(amount)} {currency}
            </span>
          </div>
          <div className="hitlSimCard-detail">
            <span>Nossa taxa</span>
            <span>
              {formatDecimal(withdrawFee)} {currency}
            </span>
          </div>
          <div className="hitlSimCard-detail">
            <span>Total debitado</span>
            <span>
              {formatDecimal(totalDebit)} {currency}
            </span>
          </div>
          <div className="hitlSimCard-detail">
            <span>Rede</span>
            <span>{network || "—"}</span>
          </div>
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Destino</span>
            <span className="hitlSimCard-mono">{address || "—"}</span>
          </div>
          {tag && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Tag/Memo</span>
              <span className="hitlSimCard-mono">{tag}</span>
            </div>
          )}
          {withdrawRequestId && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Solicitação</span>
              <span className="hitlSimCard-mono">{withdrawRequestId}</span>
            </div>
          )}
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              expired || approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !expired && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar saque"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

type LinearSimulation = {
  positionSide: "LONG" | "SHORT";
  qty: string;
  notional: string;
  leverage: string;
  marginMode: string;
  initialMargin: string;
  marginCurrency: string;
  liqPrice: string;
  liqPricePercent: string;
  entryPrice: string;
  takeProfit?: string;
  stopLoss?: string;
};

function getLinearSimulation(
  args: Record<string, unknown>,
): LinearSimulation | null {
  const linear = args.linear;

  if (!linear || typeof linear !== "object") return null;

  const l = linear as Record<string, unknown>;

  if (l.positionSide !== "LONG" && l.positionSide !== "SHORT") return null;

  return l as unknown as LinearSimulation;
}

function FutureApprovalHitlCard({
  request,
  status,
  metadata,
  onDecision,
  onExpired,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  const pairName = String(args.pairName ?? "");
  const inputCurrency = String(args.inputCurrency ?? "");
  const outputCurrency = String(args.outputCurrency ?? "");
  const fee = String(args.fee ?? "");
  const feePercent = String(args.feePercent ?? "");
  const quoteId = String(args.quoteId ?? "");

  const linear = getLinearSimulation(args);
  const isLong = linear?.positionSide === "LONG";

  // O ativo (baseCoin) muda de lado conforme a direção: LONG recebe o ativo
  // em outputCurrency; SHORT envia o ativo em inputCurrency.
  const assetCurrency = isLong ? outputCurrency : inputCurrency;

  const expiresAtSource = metadata?.expiresIn ?? args.expiresIn;
  const expiresAt = parseExpiresAtMs(expiresAtSource);
  const hasExpiry = Number.isFinite(expiresAt) && expiresAt > 0;

  const [now, setNow] = useState(() => Date.now());
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!hasExpiry) return;
    const handle = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(handle);
  }, [hasExpiry]);

  const secondsLeft = hasExpiry
    ? Math.max(0, Math.floor((expiresAt - now) / 1000))
    : 0;

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  const hasFinalStatus = isRejected || isApproved;
  const isExpiredByStatus = status === "expired";
  const isExpiredByTimer = !hasFinalStatus && hasExpiry && secondsLeft <= 0;
  const expired = isExpiredByStatus || isExpiredByTimer;

  useEffect(() => {
    if (isExpiredByTimer && !expiredFiredRef.current) {
      expiredFiredRef.current = true;
      onExpired?.();
    }
  }, [isExpiredByTimer, onExpired]);

  const disableActions = submitting || isRejected || expired || isApproved;

  const directionLabel = isLong ? "Long" : "Short";

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Aberta"
    : isRejected
    ? "Cancelada"
    : expired
    ? "Expirada"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Aprovado, mas a abertura da posição falhou: ${failureReason}`
      : "Aprovado, mas a abertura da posição falhou."
    : isApproved
    ? "Posição aberta."
    : isRejected
    ? "Posição cancelada."
    : submitting
    ? "Enviando decisão ao agente…"
    : expired
    ? "Cotação expirada. Peça uma nova simulação."
    : null;

  const handleConfirm = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  const handleCancel = () => {
    if (submitting || isRejected || expired) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : expired
    ? " hitlSimCard--expired"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Posição de futuros aprovada mas falhou na execução (HITL)"
            : isApproved
            ? "Posição de futuros aberta (HITL)"
            : isRejected
            ? "Posição de futuros cancelada (HITL)"
            : expired
            ? "Cotação de futuros expirada (HITL)"
            : "Aprovação de posição de futuros (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : expired
                ? "expired"
                : "buy"
            }`}
          >
            {approvedButFailed
              ? "!"
              : isApproved
              ? "✓"
              : expired
              ? "✕"
              : isLong
              ? "↗"
              : "↘"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Confirmar {directionLabel}
              {pairName ? ` — ${pairName}` : ""}
              {linear ? ` (${linear.leverage}x)` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Revise a simulação antes de abrir a posição."}
            </div>
          </div>

          {!badge && hasExpiry && (
            <span className="hitlSimCard-countdown">
              {secondsLeft}s
            </span>
          )}

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : isRejected
                  ? "hitlSimCard-countdown--rejected"
                  : "hitlSimCard-countdown--expired"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-amounts">
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Direção</div>
            <div className="hitlSimCard-amountValue">
              {directionLabel} {linear ? `${linear.leverage}x` : ""}
            </div>
          </div>
          <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
          <div className="hitlSimCard-amount">
            <div className="hitlSimCard-amountLabel">Tamanho</div>
            <div className="hitlSimCard-amountValue">
              {linear ? formatDecimal(linear.qty) : "—"} {assetCurrency}
            </div>
          </div>
        </div>

        {linear && (
          <div className="hitlSimCard-details">
            <div className="hitlSimCard-detail">
              <span>Nocional</span>
              <span>
                {formatDecimal(linear.notional)} {linear.marginCurrency}
              </span>
            </div>
            <div className="hitlSimCard-detail">
              <span>Margem inicial</span>
              <span>
                {formatDecimal(linear.initialMargin)} {linear.marginCurrency}
                {` (${linear.marginMode})`}
              </span>
            </div>
            <div className="hitlSimCard-detail">
              <span>Preço de entrada</span>
              <span>{formatDecimal(linear.entryPrice)}</span>
            </div>
            <div className="hitlSimCard-detail">
              <span>Preço de liquidação</span>
              <span>
                {formatDecimal(linear.liqPrice)}
                {linear.liqPricePercent
                  ? ` (${formatDecimal(linear.liqPricePercent)}%)`
                  : ""}
              </span>
            </div>
            {linear.takeProfit && (
              <div className="hitlSimCard-detail">
                <span>Take profit</span>
                <span>{formatDecimal(linear.takeProfit)}</span>
              </div>
            )}
            {linear.stopLoss && (
              <div className="hitlSimCard-detail">
                <span>Stop loss</span>
                <span>{formatDecimal(linear.stopLoss)}</span>
              </div>
            )}
            <div className="hitlSimCard-detail">
              <span>Taxa</span>
              <span>
                {formatDecimal(fee)} {linear.marginCurrency}
                {feePercent ? ` (${feePercent}%)` : ""}
              </span>
            </div>
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Quote ID</span>
              <span className="hitlSimCard-mono">{quoteId || "—"}</span>
            </div>
          </div>
        )}

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              expired || approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !expired && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : `Confirmar ${directionLabel}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CancelOrderApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Campos vindos do `list_orders` que o agente repassa só para exibição. O
  // cancelamento é validado no servidor pelo `orderId`.
  const orderId = String(args.orderId ?? "");
  const baseCoin = String(args.baseCoin ?? "");
  const quoteCoin = String(args.quoteCoin ?? "");
  const symbol = baseCoin && quoteCoin ? `${baseCoin}/${quoteCoin}` : baseCoin || quoteCoin;
  const side = String(args.side ?? "");
  const orderType = String(args.orderType ?? "");
  const qty = String(args.qty ?? "");
  const price = String(args.price ?? "");

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  // Cancelamento não tem cotação/expiração: o agente já tem o orderId.
  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  // Aqui a semântica é invertida em relação aos outros cards: "approve"
  // significa CANCELAR a ordem; "reject" significa MANTER a ordem.
  const disableActions = submitting || isRejected || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Ordem cancelada"
    : isRejected
    ? "Mantida"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível cancelar a ordem: ${failureReason}`
      : "Não foi possível cancelar a ordem."
    : isApproved
    ? "Ordem cancelada. O saldo reservado foi liberado."
    : isRejected
    ? "A ordem foi mantida."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → cancela a ordem
  const handleConfirmCancel = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → mantém a ordem
  const handleKeepOrder = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Cancelamento de ordem falhou (HITL)"
            : isApproved
            ? "Ordem cancelada (HITL)"
            : isRejected
            ? "Ordem mantida (HITL)"
            : "Confirmação de cancelamento de ordem (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "expired"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "✕"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Cancelar ordem{symbol ? ` — ${symbol}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme o cancelamento da ordem aberta."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-details">
          {(side || orderType) && (
            <div className="hitlSimCard-detail">
              <span>Ordem</span>
              <span>
                {[side, orderType].filter(Boolean).join(" ") || "—"}
              </span>
            </div>
          )}
          {qty && (
            <div className="hitlSimCard-detail">
              <span>Quantidade</span>
              <span>{formatDecimal(qty)}</span>
            </div>
          )}
          {price && (
            <div className="hitlSimCard-detail">
              <span>Preço</span>
              <span>{formatDecimal(price)}</span>
            </div>
          )}
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Order ID</span>
            <span className="hitlSimCard-mono">{orderId || "—"}</span>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleKeepOrder}
              disabled={submitting}
            >
              Manter ordem
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirmCancel}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar cancelamento"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

type ClosePositionResult = {
  orderId: string;
  closedQty: string;
  remainingQty: string;
  realizedPnl: string;
  fullyClosed: boolean;
  orderStatus: string;
};

function getClosePositionResult(
  metadata?: ChatInterruptMetadata,
): ClosePositionResult | null {
  const output = metadata?.toolOutput;

  if (!output || typeof output !== "object") return null;

  const record = output as Record<string, unknown>;
  const position = record.position;

  if (!position || typeof position !== "object") return null;

  const p = position as Record<string, unknown>;

  if (typeof p.closedQty !== "string") return null;

  return p as unknown as ClosePositionResult;
}

function ClosePositionApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Campos vindos do `list_orders` que o agente repassa só para exibição. O
  // fechamento é validado no servidor pelo `orderId`.
  const orderId = String(args.orderId ?? "");
  const baseCoin = String(args.baseCoin ?? "");
  const quoteCoin = String(args.quoteCoin ?? "");
  const symbol = baseCoin && quoteCoin ? `${baseCoin}/${quoteCoin}` : baseCoin || quoteCoin;
  const side = String(args.side ?? "");
  // `positionQty` é o tamanho com que a posição foi ABERTA e não encolhe em
  // fechamentos parciais; `remainingQty` é o que ainda está aberto — é ele o
  // limite real de fechamento. Os dois são exibidos: o total dá contexto, o
  // restante é o que de fato pode ser fechado.
  const positionQty = String(args.positionQty ?? "");
  const remainingQty = String(args.remainingQty ?? "");
  const hasPartialHistory = Boolean(
    positionQty && remainingQty && !sameQuantity(positionQty, remainingQty),
  );
  const entryPrice = String(args.entryPrice ?? "");
  const leverage = String(args.leverage ?? "");
  // `qty` presente = fechamento PARCIAL; ausente = fecha tudo.
  const closeQty = String(args.qty ?? "");
  const isPartial = closeQty.length > 0;

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  // Fechamento não tem cotação/expiração: o agente já tem o orderId.
  const result = getClosePositionResult(metadata);

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  // Aqui a semântica é invertida em relação aos cards de cotação: "approve"
  // significa FECHAR a posição; "reject" significa MANTER a posição.
  const disableActions = submitting || isRejected || isApproved;

  const fullyClosed = result?.fullyClosed ?? false;
  const realizedPnl = result ? parseFloat(result.realizedPnl) : NaN;
  const pnlPositive = Number.isFinite(realizedPnl) && realizedPnl >= 0;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? (fullyClosed ? "Posição fechada" : "Parcial fechada")
    : isRejected
    ? "Mantida"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível fechar a posição: ${failureReason}`
      : "Não foi possível fechar a posição."
    : isApproved && result
    ? (fullyClosed
      ? `Posição fechada. P&L realizado: ${formatDecimal(result.realizedPnl)}.`
      : `Fechamento parcial de ${formatDecimal(result.closedQty)}. ` +
        `Restam ${formatDecimal(result.remainingQty)}. ` +
        `P&L realizado: ${formatDecimal(result.realizedPnl)}.`)
    : isApproved
    ? "Posição fechada."
    : isRejected
    ? "A posição foi mantida."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → fecha a posição
  const handleConfirmClose = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → mantém a posição
  const handleKeepPosition = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Fechamento de posição falhou (HITL)"
            : isApproved
            ? "Posição fechada (HITL)"
            : isRejected
            ? "Posição mantida (HITL)"
            : "Confirmação de fechamento de posição (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "expired"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "✕"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              {isPartial ? "Fechar parcial" : "Fechar posição"}
              {symbol ? ` — ${symbol}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme o fechamento da posição de futuros."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-details">
          {side && (
            <div className="hitlSimCard-detail">
              <span>Direção</span>
              <span>
                {side}
                {leverage ? ` ${leverage}x` : ""}
              </span>
            </div>
          )}
          {positionQty && (
            <div className="hitlSimCard-detail">
              <span>{hasPartialHistory ? "Tamanho original" : "Tamanho aberto"}</span>
              <span>{formatDecimal(positionQty)}</span>
            </div>
          )}
          {remainingQty && hasPartialHistory && (
            <div className="hitlSimCard-detail">
              <span>Ainda aberto</span>
              <span>{formatDecimal(remainingQty)}</span>
            </div>
          )}
          <div className="hitlSimCard-detail">
            <span>Fechando</span>
            <span>
              {isPartial ? formatDecimal(closeQty) : "Tudo"}
            </span>
          </div>
          {entryPrice && (
            <div className="hitlSimCard-detail">
              <span>Preço de entrada</span>
              <span>{formatDecimal(entryPrice)}</span>
            </div>
          )}
          {result && (
            <div className="hitlSimCard-detail">
              <span>P&L realizado</span>
              <span
                className={pnlPositive
                  ? "hitlSimCard-status--approved"
                  : "hitlSimCard-status--error"}
              >
                {formatDecimal(result.realizedPnl)}
              </span>
            </div>
          )}
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Order ID</span>
            <span className="hitlSimCard-mono">{orderId || "—"}</span>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleKeepPosition}
              disabled={submitting}
            >
              Manter posição
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirmClose}
              disabled={disableActions}
            >
              {submitting
                ? "Enviando..."
                : isPartial
                ? "Confirmar fechamento parcial"
                : "Confirmar fechamento"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Rótulos pt-BR das periodicidades do plano DCAi. Novos planos só aceitam
// WEEKLY/MONTHLY; os demais aparecem em planos legados ou cenários de QA.
const DCAI_FREQUENCY_LABEL: Record<string, string> = {
  WEEKLY: "Semanal",
  MONTHLY: "Mensal",
  BIWEEKLY: "Quinzenal",
  QUARTERLY: "Trimestral",
  HALF_HOURLY: "A cada 30 min",
  MINUTELY: "A cada minuto",
};

function CreateDcaiApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Parâmetros do plano que o agente propõe. A criação é validada no servidor.
  const name = String(args.name ?? "");
  const baseCoin = String(args.baseCoin ?? "");
  const quoteCoin = String(args.quoteCoin ?? "");
  const contributionAmount = String(args.contributionAmount ?? "");
  const frequency = String(args.frequency ?? "");
  const frequencyLabel = frequency
    ? DCAI_FREQUENCY_LABEL[frequency] ?? frequency
    : "";

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  // Criação de plano não tem cotação/expiração: os parâmetros vêm do agente.
  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  const disableActions = submitting || isRejected || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Plano criado"
    : isRejected
    ? "Cancelado"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível criar o plano: ${failureReason}`
      : "Não foi possível criar o plano DCAi."
    : isApproved
    ? "Plano DCAi criado. O primeiro ciclo será processado em instantes."
    : isRejected
    ? "A criação do plano foi cancelada."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → cria o plano
  const handleConfirmCreate = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → cancela a criação
  const handleCancel = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Criação de plano DCAi falhou (HITL)"
            : isApproved
            ? "Plano DCAi criado (HITL)"
            : isRejected
            ? "Criação de plano DCAi cancelada (HITL)"
            : "Confirmação de criação de plano DCAi (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "buy"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "🤖"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Plano DCAi{baseCoin ? ` — ${baseCoin}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme a criação do plano de DCA Inteligente."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        {contributionAmount && (
          <div className="hitlSimCard-amounts">
            <div className="hitlSimCard-amount">
              <div className="hitlSimCard-amountLabel">Aporte por ciclo</div>
              <div className="hitlSimCard-amountValue">
                {formatDecimal(contributionAmount)} {quoteCoin}
              </div>
            </div>
            <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
            <div className="hitlSimCard-amount">
              <div className="hitlSimCard-amountLabel">Comprando</div>
              <div className="hitlSimCard-amountValue">{baseCoin || "—"}</div>
            </div>
          </div>
        )}

        <div className="hitlSimCard-details">
          {frequencyLabel && (
            <div className="hitlSimCard-detail">
              <span>Frequência</span>
              <span>{frequencyLabel}</span>
            </div>
          )}
          {name && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Nome do plano</span>
              <span>{name}</span>
            </div>
          )}
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirmCreate}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar plano"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CloseDcaiApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Campos vindos do `list_dcai_plans` que o agente repassa só para exibição. O
  // encerramento é validado no servidor pelo `planId`.
  const planId = String(args.planId ?? "");
  const name = String(args.name ?? "");
  const baseCoin = String(args.baseCoin ?? "");
  const quoteCoin = String(args.quoteCoin ?? "");
  const contributionAmount = String(args.contributionAmount ?? "");
  const frequency = String(args.frequency ?? "");
  const frequencyLabel = frequency
    ? DCAI_FREQUENCY_LABEL[frequency] ?? frequency
    : "";

  // Acumulado do plano (quantidade custodiada + valor de mercado em USDT) —
  // o valor pode faltar quando não havia cotação no momento da listagem.
  const accumulatedBaseAmount = String(args.accumulatedBaseAmount ?? "");
  const accumulatedValueUsdt = String(args.accumulatedValueUsdt ?? "");
  const hasAccumulated = parseFloat(accumulatedBaseAmount) > 0;

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  // Semântica invertida (como no cancel_order): "approve" ENCERRA o plano;
  // "reject" MANTÉM o plano ativo.
  const disableActions = submitting || isRejected || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Plano encerrado"
    : isRejected
    ? "Mantido"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível encerrar o plano: ${failureReason}`
      : "Não foi possível encerrar o plano DCAi."
    : isApproved
    ? "Plano DCAi encerrado. O ativo custodiado será devolvido à sua carteira."
    : isRejected
    ? "O plano foi mantido ativo."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → encerra o plano
  const handleConfirmClose = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → mantém o plano
  const handleKeepPlan = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Encerramento de plano DCAi falhou (HITL)"
            : isApproved
            ? "Plano DCAi encerrado (HITL)"
            : isRejected
            ? "Plano DCAi mantido (HITL)"
            : "Confirmação de encerramento de plano DCAi (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "expired"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "✕"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Encerrar plano DCAi{baseCoin ? ` — ${baseCoin}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme o encerramento do plano de DCA Inteligente."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        {hasAccumulated && (
          <div className="hitlSimCard-amounts">
            <div className="hitlSimCard-amount">
              <div className="hitlSimCard-amountLabel">Ativo acumulado</div>
              <div className="hitlSimCard-amountValue">
                {formatDecimal(accumulatedBaseAmount)}
                {baseCoin ? ` ${baseCoin}` : ""}
              </div>
            </div>
            <div className="hitlSimCard-arrow" aria-hidden="true">→</div>
            <div className="hitlSimCard-amount">
              <div className="hitlSimCard-amountLabel">Valor estimado</div>
              <div className="hitlSimCard-amountValue">
                {accumulatedValueUsdt
                  ? `≈ ${formatDecimal(accumulatedValueUsdt)} USDT`
                  : "—"}
              </div>
            </div>
          </div>
        )}

        <div className="hitlSimCard-details">
          {contributionAmount && (
            <div className="hitlSimCard-detail">
              <span>Aporte por ciclo</span>
              <span>
                {formatDecimal(contributionAmount)}
                {quoteCoin ? ` ${quoteCoin}` : ""}
              </span>
            </div>
          )}
          {frequencyLabel && (
            <div className="hitlSimCard-detail">
              <span>Frequência</span>
              <span>{frequencyLabel}</span>
            </div>
          )}
          {name && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>Nome do plano</span>
              <span>{name}</span>
            </div>
          )}
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Plan ID</span>
            <span className="hitlSimCard-mono">{planId || "—"}</span>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleKeepPlan}
              disabled={submitting}
            >
              Manter plano
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmBuy"
              onClick={handleConfirmClose}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar encerramento"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function scheduledActionDateLabel(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Lê `metadata.toolOutput.action.id` após a execução da tool. */
function getScheduledActionResultId(
  metadata?: ChatInterruptMetadata,
): string | null {
  const output = metadata?.toolOutput;
  if (!output || typeof output !== "object") return null;
  const action = (output as Record<string, unknown>).action;
  if (!action || typeof action !== "object") return null;
  const id = (action as Record<string, unknown>).id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

function CreateScheduledActionApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Parâmetros do alerta que o agente propõe. A criação (símbolo suportado,
  // cap de 5 ativas, direção do gatilho) é validada no servidor.
  const baseCoin = String(args.baseCoin ?? "");
  const targetPrice = String(args.targetPrice ?? "");
  const recurring = args.recurring === true;
  const expiresAt = typeof args.expiresAt === "string" ? args.expiresAt : "";

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  // Criação de ação agendada não tem cotação/expiração de aprovação.
  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;
  const createdActionId = isApproved && !toolFailed
    ? getScheduledActionResultId(metadata)
    : null;

  const disableActions = submitting || isRejected || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Criada"
    : isRejected
    ? "Cancelada"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível criar a ação: ${failureReason}`
      : "Não foi possível criar a ação agendada."
    : isApproved
    ? "Ação agendada criada. O monitor de preço acompanha o mercado a partir de agora."
    : isRejected
    ? "A criação da ação foi cancelada."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → cria a ação
  const handleConfirm = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → cancela a criação
  const handleCancel = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Criação de ação agendada falhou (HITL)"
            : isApproved
            ? "Ação agendada criada (HITL)"
            : isRejected
            ? "Criação de ação agendada cancelada (HITL)"
            : "Confirmação de criação de ação agendada (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "neutral"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "🎯"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Alerta de preço{baseCoin ? ` — ${baseCoin}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme o alerta de preço."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-details">
          {targetPrice && (
            <div className="hitlSimCard-detail">
              <span>Preço-alvo</span>
              <span>{formatDecimal(targetPrice)} USDT</span>
            </div>
          )}
          <div className="hitlSimCard-detail">
            <span>Recorrência</span>
            <span>{recurring ? "Recorrente" : "Uma única vez"}</span>
          </div>
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>Expira em</span>
            <span>
              {expiresAt ? scheduledActionDateLabel(expiresAt) : "Sem expiração"}
            </span>
          </div>
          {createdActionId && (
            <div className="hitlSimCard-detail hitlSimCard-detail--muted">
              <span>ID</span>
              <span className="hitlSimCard-mono">{createdActionId}</span>
            </div>
          )}
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmNeutral"
              onClick={handleConfirm}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar alerta"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DeleteScheduledActionApprovalCard({
  request,
  status,
  metadata,
  onDecision,
}: {
  request: AgentInterruptActionRequest;
  status: HitlStatus;
  metadata?: ChatInterruptMetadata;
  onDecision: (input: HitlDecision) => any | Promise<any>;
  onExpired?: () => void;
}) {
  const args = (request.args ?? {}) as Record<string, unknown>;

  // Campos vindos do `list_scheduled_actions` que o agente repassa só para
  // exibição. O cancelamento é validado no servidor pelo `actionId`.
  const actionId = String(args.actionId ?? "");
  const baseCoin = String(args.baseCoin ?? "");
  const targetPrice = String(args.targetPrice ?? "");
  const direction = String(args.direction ?? "");
  const recurring = args.recurring === true;

  const triggerLabel = targetPrice
    ? `${
      direction === "ABOVE"
        ? "acima de "
        : direction === "BELOW"
        ? "abaixo de "
        : ""
    }${formatDecimal(targetPrice)} USDT`
    : "";

  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const submitting = status === "submitting";

  const toolFailed = metadata?.toolStatus === "failed";
  const approvedButFailed = isApproved && toolFailed;
  const failureReason = typeof metadata?.failureReason === "string"
    ? metadata.failureReason
    : null;

  // Semântica invertida (como no cancel_order): "approve" EXCLUI a ação;
  // "reject" a mantém ativa.
  const disableActions = submitting || isRejected || isApproved;

  const badge = approvedButFailed
    ? "Falhou"
    : isApproved
    ? "Excluída"
    : isRejected
    ? "Mantida"
    : null;

  const statusMessage = approvedButFailed
    ? failureReason
      ? `Não foi possível excluir a ação: ${failureReason}`
      : "Não foi possível excluir a ação agendada."
    : isApproved
    ? "Ação agendada excluída."
    : isRejected
    ? "A ação foi mantida."
    : submitting
    ? "Enviando decisão ao agente…"
    : null;

  // approve → exclui a ação
  const handleConfirmDelete = () => {
    if (disableActions) return;
    void onDecision({ name: request.name, decision: "approve" });
  };

  // reject → mantém a ação
  const handleKeep = () => {
    if (submitting || isRejected || isApproved) return;
    void onDecision({ name: request.name, decision: "reject" });
  };

  const cardModifier = approvedButFailed
    ? " hitlSimCard--failed"
    : isApproved
    ? " hitlSimCard--approved"
    : isRejected
    ? " hitlSimCard--rejected"
    : "";

  return (
    <div className="hitlWrap">
      <div
        className={`hitlSimCard${cardModifier}`}
        role="group"
        aria-label={
          approvedButFailed
            ? "Exclusão de ação agendada falhou (HITL)"
            : isApproved
            ? "Ação agendada excluída (HITL)"
            : isRejected
            ? "Ação agendada mantida (HITL)"
            : "Confirmação de exclusão de ação agendada (HITL)"
        }
      >
        <div className="hitlSimCard-head">
          <div
            className={`hitlSimCard-icon hitlSimCard-icon--${
              approvedButFailed
                ? "failed"
                : isApproved
                ? "approved"
                : "neutral"
            }`}
          >
            {approvedButFailed ? "!" : isApproved ? "✓" : "🗑"}
          </div>
          <div className="hitlSimCard-headText">
            <div className="hitlSimCard-title">
              Excluir ação agendada{baseCoin ? ` — ${baseCoin}` : ""}
            </div>
            <div className="hitlSimCard-sub">
              {request.description?.trim()
                ? request.description
                : "Confirme a exclusão da ação agendada."}
            </div>
          </div>

          {badge && (
            <span
              className={`hitlSimCard-countdown ${
                approvedButFailed
                  ? "hitlSimCard-countdown--failed"
                  : isApproved
                  ? "hitlSimCard-countdown--approved"
                  : "hitlSimCard-countdown--rejected"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <div className="hitlSimCard-details">
          {triggerLabel && (
            <div className="hitlSimCard-detail">
              <span>Gatilho</span>
              <span>{triggerLabel}</span>
            </div>
          )}
          <div className="hitlSimCard-detail">
            <span>Recorrência</span>
            <span>{recurring ? "Recorrente" : "Uma única vez"}</span>
          </div>
          <div className="hitlSimCard-detail hitlSimCard-detail--muted">
            <span>ID</span>
            <span className="hitlSimCard-mono">{actionId || "—"}</span>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`hitlSimCard-status${
              approvedButFailed ? " hitlSimCard-status--error" : ""
            }${isRejected ? " hitlSimCard-status--rejected" : ""}${
              isApproved && !approvedButFailed
                ? " hitlSimCard-status--approved"
                : ""
            }`}
          >
            {statusMessage}
          </div>
        )}

        {!isRejected && !isApproved && (
          <div className="hitlSimCard-actions">
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--ghost"
              onClick={handleKeep}
              disabled={submitting}
            >
              Manter
            </button>
            <button
              type="button"
              className="hitlSimCard-btn hitlSimCard-btn--confirmNeutral"
              onClick={handleConfirmDelete}
              disabled={disableActions}
            >
              {submitting ? "Enviando..." : "Confirmar exclusão"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const hitlRenderers: Record<string, HitlRenderer> = {
  trade_approval: ({ request, status, metadata, onDecision, onExpired }) => (
    <TradeApprovalHitlCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
      onExpired={onExpired}
    />
  ),
  onramp_pix_approval: (
    { request, status, metadata, onDecision, onExpired },
  ) => (
    <OnRampPixApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
      onExpired={onExpired}
    />
  ),
  offramp_pix_approval: (
    { request, status, metadata, onDecision, onExpired },
  ) => (
    <OffRampPixApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
      onExpired={onExpired}
    />
  ),
  withdraw_onchain_approval: (
    { request, status, metadata, onDecision, onExpired },
  ) => (
    <WithdrawOnchainApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
      onExpired={onExpired}
    />
  ),
  future_approval: ({ request, status, metadata, onDecision, onExpired }) => (
    <FutureApprovalHitlCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
      onExpired={onExpired}
    />
  ),
  cancel_order_approval: ({ request, status, metadata, onDecision }) => (
    <CancelOrderApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
  close_position_approval: ({ request, status, metadata, onDecision }) => (
    <ClosePositionApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
  create_dcai_approval: ({ request, status, metadata, onDecision }) => (
    <CreateDcaiApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
  close_dcai_approval: ({ request, status, metadata, onDecision }) => (
    <CloseDcaiApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
  create_scheduled_action_approval: (
    { request, status, metadata, onDecision },
  ) => (
    <CreateScheduledActionApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
  delete_scheduled_action_approval: (
    { request, status, metadata, onDecision },
  ) => (
    <DeleteScheduledActionApprovalCard
      key={request.name}
      request={request}
      status={status}
      metadata={metadata}
      onDecision={onDecision}
    />
  ),
};

export function renderHitlRequest(ctx: HitlRendererCtx) {
  return ctx.interrupt.action_requests.map((request) => {
    const closure = hitlRenderers[request.name];

    if (!closure) {
      return null;
    }

    return closure({ ...ctx, request });
  });
}
